import { useAppLoading } from '@/hooks';
import { IManagedUserSummary, IUser } from '@/types';
import { useMemo, useState } from 'react';
import type { IUserProfileTabItem, UserProfileTabsProps } from './types';
import { useCurrentUserFeature } from '@/features/users/currentUser/userCurrentUserFeature';

/**
 * Input params for `useUserProfileTabs`.
 *
 * This hook receives only the values needed to derive tab visibility/state and
 * normalize user data for the `UserProfileTabs` presentation component.
 */
interface UseUserProfileTabsParams {
  /** Optional loading signal from the consumer (component-level async state) */
  isLoadingProp?: boolean;
  /** List of tab definitions used to build visible tab UI and initial active tab */
  tabs?: IUserProfileTabItem[];
  /** User data source (supports multiple shapes through `userObjectType`) */
  user: UserProfileTabsProps['user'];
  /** Discriminator used to map incoming user shape into a normalized `IUser` */
  userObjectType: UserProfileTabsProps['userObjectType'];
}

/** Return values from `useUserProfileTabs` hook */
interface UseUserProfileTabsReturn {
  /**
   * Currently active tab id, used to control tab selection state in the UI
   */
  activeTab: string;

  /**
   * Normalized user data in the canonical `IUser` shape,
   * used to feed downstream UI components/utilities
   */
  formattedUser: IUser | null;

	/** Formatted user name for display in the profile header */
	formattedUserName: string;

	/** Formatted user role/title for display in the profile header */
	formattedUserTitle: string;

  /**
   * Loading signal that merges local and global loading states for
   * consistent UX during data fetching
   */
  isLoading: boolean;

  /**
   * State setter for `activeTab`, used to update the active tab in
   * response to user interaction in the UI
   */
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;

  /**
   * List of tabs that should be rendered in the UI, derived
   * from input tabs and their enabled/disabled state
   */
  visibleTabs: IUserProfileTabItem[];
}

/**
 * Local feature hook for `UserProfileTabs`.
 *
 * Encapsulates all non-visual logic for:
 * - loading state composition,
 * - visible tab derivation,
 * - active tab state,
 * - and user shape normalization.
 *
 * Keeping this logic in a hook helps keep the component presentational and
 * makes behavior easier to unit test in isolation.
 */
export const useUserProfileTabs = ({
  isLoadingProp = false,
  tabs = [],
  user,
  userObjectType,
}: UseUserProfileTabsParams): UseUserProfileTabsReturn => {
  // ================================
  // Loading orchestration
  // ================================
  // Merge local loading with app/store initialization loading via shared utility hook.
  // This keeps loading behavior consistent with the rest of the app.
  const isLoading = useAppLoading(isLoadingProp);

  // ================================
  // Derived tab state
  // ================================
  // Show all tabs except those explicitly disabled (`enabled === false`).
  // If `enabled` is undefined, the tab is considered visible by default.
  const visibleTabs = useMemo(() => tabs.filter((tab) => tab.enabled !== false), [tabs]);

  // ================================
  // Active tab state
  // ================================
  // Initialize to first visible tab for a predictable initial UI state.
  // If no tabs are visible, keep the active id empty to avoid invalid selection.
  const [activeTab, setActiveTab] = useState<string>(
    visibleTabs.length > 0 ? visibleTabs[0].id : '',
  );

  // ================================
  // User normalization
  // ================================
  // Normalize polymorphic `user` input into the shared `IUser` shape expected by
  // downstream UI utilities/components (`getUserName`, `getUserRole`, `UserInfo`).
  const formattedUser: IUser | null = useMemo(() => {
    // Guard: no user provided.
    if (!user) {
      return null;
    }

    // Pass-through: already in canonical IUser shape.
    if (userObjectType === 'IUser') {
      return user as IUser;
    }

    // Mapping: convert manager summary shape into canonical IUser shape.
    // This preserves compatibility across contexts that provide reduced user objects.
    if (userObjectType === 'IManagedUserSummary') {
      const summaryUser = user as IManagedUserSummary;
      return {
        name: summaryUser.userName,
        role: summaryUser.userRole,
        email: summaryUser.userEmail,
        uuid: summaryUser.userId,
        imageUrl: summaryUser.userImage || null,
      } as IUser;
    }

    // Defensive fallback for unsupported discriminator values.
    return null;
  }, [user, userObjectType]);

	// ===============================
	// Current user feature integration
	// ================================
	// Leverage current user feature to derive display name and role title for
	// the profile header. This ensures consistent formatting and role naming.
  const { currentUserFullName, currentUserRole } = useCurrentUserFeature({
    currentUser: formattedUser,
  });

	// Memoize formatted name
  const formattedUserName = useMemo(() => {
    if (!formattedUser) return 'Unknown user';
    return currentUserFullName;
  }, [formattedUser, currentUserFullName]);

	// Memoize formatted role title
  const formattedUserTitle = useMemo(() => {
    if (!formattedUser) return 'No role assigned';
    return currentUserRole;
  }, [formattedUser, currentUserRole]);

  // ================================
  // Hook API
  // ================================
  // Return all state/derivatives consumed by `UserProfileTabs`.
  return {
    activeTab,
    formattedUser,
    formattedUserName,
    formattedUserTitle,
    isLoading,
    setActiveTab,
    visibleTabs,
  };
};

export default useUserProfileTabs;
